import { useCallback, useLayoutEffect, useMemo } from "react";
import { useFocusEffect, useNavigation, useRouter } from "expo-router";
import { useHeaderBackButton } from "@src/hooks/useHeaderBackButton";
import { useCameras } from "@src/services/api/hooks";
import { useSiteNames } from "@src/features/cameras/hooks/useSiteNames";
import { groupCamerasBySite } from "@src/features/cameras/utils/camera-group";
import { resolveCameraDisplayName } from "@src/features/cameras/utils/camera-display-name";
import { getCameraKeyString } from "@src/utils/key-parser";
import { showErrorToast } from "@src/utils/toast";
import { FEATURE_FLAGS, useBooleanFlag } from "@src/services/feature-flags/feature-flags";
import {
  useNotificationPreferences,
  useContactPhoneNumber,
  DEFAULT_NOTIFICATION_CATEGORY_PREFERENCES,
  type NotificationPreferencesFormSite,
  type CameraNotificationPreference,
  type NotificationCategory,
  type NotificationChannel,
  type NotificationChannelPreferences,
  type NotificationCategoryPreferences,
} from "@visioni/a3s-alarm-schedules/app";

function orChannels(
  a: NotificationChannelPreferences,
  b: NotificationChannelPreferences,
): NotificationChannelPreferences {
  return { email: a.email || b.email, sms: a.sms || b.sms, push: a.push || b.push };
}

/** Merges two scopes' preferences into "is this cell covered by either" — used to compute `inherited`. */
function orCategoryPreferences(
  a: NotificationCategoryPreferences,
  b: NotificationCategoryPreferences,
): NotificationCategoryPreferences {
  return {
    alarmEvent: orChannels(a.alarmEvent, b.alarmEvent),
    cameraConfigChange: orChannels(a.cameraConfigChange, b.cameraConfigChange),
  };
}

export function useNotificationPreferencesPage() {
  const navigation = useNavigation();
  const router = useRouter();
  const { headerLeft } = useHeaderBackButton();
  const isNotificationSettingsEnabled = useBooleanFlag(FEATURE_FLAGS.NOTIFICATION_SETTINGS);

  // Guards direct navigation (deep link, browser back/forward, typed URL) — the
  // settings menu entries already only render when the flag is on, but the
  // route is still reachable directly regardless of what links to it. Must be
  // useFocusEffect, not useLayoutEffect: this can render before the root
  // navigator has mounted (e.g. a direct web navigation), and replacing then
  // throws "Attempted to navigate before mounting the Root Layout component."
  // useFocusEffect only fires once the screen is actually focused inside a
  // mounted navigator.
  useFocusEffect(
    useCallback(() => {
      if (!isNotificationSettingsEnabled) {
        router.replace("/(tabs)/settings");
      }
    }, [isNotificationSettingsEnabled, router]),
  );

  useLayoutEffect(() => {
    navigation.setOptions({
      title: "Notification Preferences",
      headerBackButtonDisplayMode: "minimal",
      headerLeft,
    });
  }, [navigation, headerLeft]);

  const {
    preferences,
    isLoading: isPreferencesLoading,
    isError: isPreferencesError,
    setCategory,
  } = useNotificationPreferences();
  const { phoneNumber, setPhoneNumber, isLoading: isPhoneNumberLoading } = useContactPhoneNumber();
  // isLoading is combined: the phone field is part of this same form, so it
  // must not become interactive (and empty) before its own, separately-
  // fetched value has loaded — otherwise a focus+blur on it while still
  // loading reads as "the user cleared it" and deletes the real, not-yet-
  // displayed number.
  //
  // isError is deliberately NOT combined: a phone-number fetch failure
  // (e.g. Auth0 wiring not yet deployed to this environment) must not fail
  // the whole preferences page — the field just renders empty, same as
  // "never set one", and the rest of the form keeps working.
  const isLoading = isPreferencesLoading || isPhoneNumberLoading;
  const isError = isPreferencesError;
  const { data: cameraList } = useCameras();
  const siteNames = useSiteNames();

  const siteGroups = useMemo(() => groupCamerasBySite(cameraList ?? [], "site", siteNames), [cameraList, siteNames]);

  const allCameras = useMemo(() => {
    const item = preferences.find((p) => p.scope.kind === "allCameras");
    return item?.preferences ?? DEFAULT_NOTIFICATION_CATEGORY_PREFERENCES;
  }, [preferences]);

  const sites = useMemo<NotificationPreferencesFormSite[]>(
    () =>
      siteGroups.map((group) => {
        const item = preferences.find((p) => p.scope.kind === "site" && p.scope.siteId === group.siteId);
        return {
          siteKey: group.siteId,
          label: group.siteName,
          preferences: item?.preferences ?? DEFAULT_NOTIFICATION_CATEGORY_PREFERENCES,
          // Nothing is broader than "All Cameras" — a site's Email is already
          // covered the moment "All Cameras" Email is on.
          inherited: allCameras,
        };
      }),
    [siteGroups, preferences, allCameras],
  );

  const cameras = useMemo<CameraNotificationPreference[]>(
    () =>
      (cameraList ?? []).map((camera) => {
        const cameraKey = getCameraKeyString(camera);
        const item = preferences.find(
          (p) =>
            p.scope.kind === "camera" &&
            p.scope.siteId === camera.site.id &&
            p.scope.assetId === camera.key.asset &&
            p.scope.cameraId === camera.key.id,
        );
        const siteItem = preferences.find((p) => p.scope.kind === "site" && p.scope.siteId === camera.site.id);
        return {
          cameraKey,
          label: resolveCameraDisplayName(cameraKey, camera.nickname),
          preferences: item?.preferences ?? DEFAULT_NOTIFICATION_CATEGORY_PREFERENCES,
          // A camera is covered once either its own site or "All Cameras" is on
          // for that cell — the site's own value (not the site's already-merged
          // `inherited` display value) is enough, since "All Cameras" is OR'd in
          // here directly too.
          inherited: orCategoryPreferences(
            allCameras,
            siteItem?.preferences ?? DEFAULT_NOTIFICATION_CATEGORY_PREFERENCES,
          ),
        };
      }),
    [cameraList, preferences, allCameras],
  );

  const handleToggleError = () => {
    showErrorToast({ message: "Couldn't save that change. Please try again." });
  };

  const onToggleAllCamerasChannel = (
    category: NotificationCategory,
    channel: NotificationChannel,
    enabled: boolean,
  ) => {
    void setCategory({ kind: "allCameras" }, category, channel, enabled).catch(handleToggleError);
  };

  const onToggleSiteChannel = (
    siteKey: string,
    category: NotificationCategory,
    channel: NotificationChannel,
    enabled: boolean,
  ) => {
    void setCategory({ kind: "site", siteId: siteKey }, category, channel, enabled).catch(handleToggleError);
  };

  const onToggleCameraChannel = (
    cameraKey: string,
    category: NotificationCategory,
    channel: NotificationChannel,
    enabled: boolean,
  ) => {
    const camera = (cameraList ?? []).find((c) => getCameraKeyString(c) === cameraKey);
    if (!camera) return;
    void setCategory(
      { kind: "camera", siteId: camera.site.id, assetId: camera.key.asset, cameraId: camera.key.id },
      category,
      channel,
      enabled,
    ).catch(handleToggleError);
  };

  const onSavePhoneNumber = (phone: string | null) => {
    void setPhoneNumber(phone).catch(handleToggleError);
  };

  return {
    isNotificationSettingsEnabled,
    isLoading,
    isError,
    allCameras,
    sites,
    cameras,
    onToggleAllCamerasChannel,
    onToggleSiteChannel,
    onToggleCameraChannel,
    phoneNumber,
    onSavePhoneNumber,
  };
}
